import { convertValue, getDisplayUnit } from '../../../utils/unitConvert.js';

const GAUGE_APPEARANCE_FIELDS = new Set([
  'arc_span_deg', 'background_color', 'background_alpha', 'text_color',
  'value_color', 'fill_color', 'warn_color', 'show_ticks', 'font_size_scale',
]);

function nativeUnit(channel) {
  return channel?.unit || (/(^|[^a-z])rpm([^a-z]|$)/i.test(channel?.name || '') ? 'rpm' : '');
}

export function gaugeValueTransform(channel, preferences = {}) {
  const prefs = { speed: 'mph', temp: 'F', pressure: 'psi', ...preferences };
  const native = nativeUnit(channel);
  const offset = convertValue(0, native, prefs);
  const scale = convertValue(1, native, prefs) - offset;
  return { unit: getDisplayUnit(native, prefs), scale, offset };
}

export function gaugeChannelPatch(overlay, channel, preferences) {
  return {
    channel: channel?.name || '',
    label: !overlay.label || overlay.label === overlay.channel ? channel?.name || '' : overlay.label,
    ...gaugeValueTransform(channel, preferences),
    // A new channel has different physical ranges; let telemetry determine them.
    min_value: null, max_value: null, warn_value: null, redline_value: null,
    dial_number_divisor: 1,
  };
}

export function gaugeStylePatch(overlay, style) {
  const patch = { style: style?.id || '' };
  for (const [field, value] of Object.entries(style?.defaults || {})) {
    // Also protects against an older renderer still advertising RPM defaults.
    if (GAUGE_APPEARANCE_FIELDS.has(field) && overlay[field] == null) patch[field] = value;
  }
  return patch;
}

export function repairGaugeSettings(overlay, channels, preferences) {
  if (overlay.type !== 'gauge') return overlay;
  const channel = channels.find(c => c.name === overlay.channel);
  if (!channel) return overlay;
  const native = nativeUnit(channel);
  const rpm = /^rpm$/i.test(native.trim());
  // Repair the incompatible caption left by the old style picker. Only discard
  // ranges matching its known tachometer presets; keep custom limits and labels.
  if (!rpm && /^rpm$/i.test((overlay.unit || '').trim())) {
    const fixed = { ...overlay, ...gaugeValueTransform(channel, preferences), dial_number_divisor: 1 };
    if ([7000, 8000, 9000, 10000].includes(overlay.max_value)) fixed.max_value = null;
    if ([6000, 6500, 7000, 7400, 8000, 9000].includes(overlay.redline_value)) fixed.redline_value = null;
    return fixed;
  }
  // Existing gauges with no caption/transform can safely inherit their channel.
  if (overlay.unit == null && overlay.scale == null && overlay.offset == null) {
    return { ...overlay, ...gaugeDisplayUnitPatch(overlay, channel, preferences) };
  }
  return overlay;
}

function gaugeDisplayUnitPatch(overlay, channel, preferences) {
  const next = gaugeValueTransform(channel, preferences);
  const oldScale = overlay.scale ?? 1;
  const oldOffset = overlay.offset ?? 0;
  for (const field of ['min_value', 'max_value', 'warn_value', 'redline_value']) {
    if (overlay[field] != null && oldScale !== 0) {
      next[field] = Math.round(((overlay[field] - oldOffset) / oldScale * next.scale + next.offset) * 100) / 100;
    }
  }
  return next;
}

export function gaugeSpeedUnitPatch(overlay, channel, speed) {
  return gaugeDisplayUnitPatch(overlay, channel, { speed });
}
